import { type ComponentProps, createContext, type ReactNode, useContext } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { cn } from '@/util/utils';

type TableProps = ComponentProps<'table'> & {
    /** Rows open something: pointer cursor and a hover fill. */
    interactive?: boolean;
    /**
     * Below 768 the table stops being a table and each row becomes a card. Cells declare
     * their role with `data-stack` (toggle, title, status, action, meta with data-label, pick, hide);
     * a cell without one is hidden. See src/styles/table.css.
     */
    stack?: boolean;
    /**
     * `fixed`: column widths come from the header cells, not from whichever page's content is
     * longest, so a register does not re-flow as you page through it. Give every column but the
     * flexible one a width on its TableHead.
     */
    layout?: 'auto' | 'fixed';
    /**
     * Names the sideways scroll the table sits in, for a matrix too wide for a phone that cannot
     * stack: the scroll becomes a labelled region a keyboard can focus and scroll with the arrows,
     * as the mobile contract asks of a local scroll.
     */
    region?: string;
    /**
     * Runs the table out to the edges of the card padding it sits in, from 768 up, and pads its
     * outer columns by that padding instead, so the first column lines up with the card's title
     * as on the lookbook register (2758:117). Below 768 a stacked register keeps the inset.
     * Defaults to what an enclosing BleedTables says, so a register's table bleeds unasked.
     */
    bleed?: boolean;
};

const BleedContext = createContext(false);

/**
 * Makes the table inside it bleed by default, for a layout that owns the card but not the table,
 * as the shared Register does (ALS-326). A table resets it for whatever it holds, so a table
 * nested in an expansion row keeps its own inset.
 */
function BleedTables({ children }: { children: ReactNode }) {
    return <BleedContext.Provider value>{children}</BleedContext.Provider>;
}

/**
 * .data-table, the shared register table, drawn as the lookbook register draws it (ALS-326): a
 * 32px header on the recessed surface closed by a hairline, its labels in the condensed label
 * face the buttons use, 55px rows divided by hairlines, 8px above and below every cell and 12px
 * beside it, with 18px at the leading edge.
 * A real table, so it is navigable and announced; the wrapper scrolls it sideways when it
 * has to, with paint containment so the page never grows wider than the viewport.
 */
function Table({
    className,
    interactive,
    stack,
    layout = 'auto',
    region,
    bleed: bleedProp,
    ref,
    ...props
}: TableProps) {
    const inherited = useContext(BleedContext);
    const bleed = bleedProp ?? inherited;
    return (
        <BleedContext.Provider value={false}>
            <div
                data-slot="table-container"
                role={region ? 'region' : undefined}
                aria-label={region}
                tabIndex={region ? 0 : undefined}
                className={cn(
                    'relative w-full overflow-x-auto [contain:paint]',
                    bleed && 'md:-mx-(--layout-card-padding) md:w-auto',
                    region &&
                        'focus-visible:outline-ring outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid',
                )}
            >
                <table
                    data-slot="table"
                    data-interactive={interactive || undefined}
                    className={cn(
                        'group/table w-full min-w-0 caption-bottom border-collapse text-sm',
                        stack && 'table-stack',
                        layout === 'fixed' && 'md:table-fixed',
                        bleed &&
                            'md:[&_td:first-child]:pl-(--layout-card-padding) md:[&_td:last-child]:pr-(--layout-card-padding) md:[&_th:first-child]:pl-(--layout-card-padding) md:[&_th:last-child]:pr-(--layout-card-padding)',
                        className,
                    )}
                    ref={ref}
                    {...props}
                />
            </div>
        </BleedContext.Provider>
    );
}

function TableHeader({ className, ref, ...props }: ComponentProps<'thead'>) {
    return <thead data-slot="table-header" className={cn(className)} ref={ref} {...props} />;
}

function TableBody({ className, ref, ...props }: ComponentProps<'tbody'>) {
    return <tbody data-slot="table-body" className={cn(className)} ref={ref} {...props} />;
}

function TableFooter({ className, ref, ...props }: ComponentProps<'tfoot'>) {
    return (
        <tfoot
            data-slot="table-footer"
            className={cn('bg-muted border-border border-t font-medium', className)}
            ref={ref}
            {...props}
        />
    );
}

function TableRow({ className, ref, ...props }: ComponentProps<'tr'>) {
    return (
        <tr
            data-slot="table-row"
            className={cn(
                'border-border border-t first:border-t-0 [thead_&]:border-t-0',
                'group-data-interactive/table:hover:bg-muted group-data-interactive/table:cursor-pointer',
                'data-[state=selected]:bg-muted transition-colors duration-(--duration-fast)',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

function TableHead({ className, ref, ...props }: ComponentProps<'th'>) {
    return (
        <th
            data-slot="table-head"
            className={cn(
                'bg-muted text-muted-foreground border-border text-label-md h-8 border-b px-3 py-2 text-left align-middle whitespace-nowrap first:pl-[18px]',
                // A select-all box is a block, so it does not sit on the label's baseline and grow
                // the 32px header by the line box under it (ALS-326).
                '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:block',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

function TableCell({ className, ref, ...props }: ComponentProps<'td'>) {
    return (
        <td
            data-slot="table-cell"
            className={cn(
                'h-[55px] px-3 py-2 align-middle text-sm first:pl-[18px]',
                '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

type TableSortHeadProps = ComponentProps<'th'> & {
    /** How this column orders the rows right now; null while it does not. */
    direction: 'asc' | 'desc' | null;
    onSort: () => void;
};

/**
 * .th-sort, a header that sorts: the whole label is the button, set exactly like its
 * neighbours. It takes the content colour on hover and while it is the sort, and the arrow
 * appears only then. The `th` carries aria-sort, so the state is announced without extra words.
 */
function TableSortHead({ direction, onSort, className, children, ...props }: TableSortHeadProps) {
    const Arrow = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : null;
    return (
        <TableHead
            aria-sort={
                direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
            }
            // On a touch screen the button is the 44px target the mobile contract asks for, so it
            // fills the header instead of being padded on top of (ALS-326).
            className={cn('pointer-coarse:py-0', className)}
            {...props}
        >
            <button
                type="button"
                data-sorted={direction ?? undefined}
                onClick={onSort}
                className={cn(
                    '-mx-1 inline-flex cursor-pointer items-center gap-1 rounded-xs px-1 text-inherit uppercase pointer-coarse:min-h-11',
                    'hover:text-foreground data-sorted:text-foreground transition-colors duration-(--duration-fast)',
                    'focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-solid',
                    '[&>svg]:size-3.5 [&>svg]:shrink-0',
                )}
            >
                {children}
                {Arrow ? <Arrow aria-hidden /> : null}
            </button>
        </TableHead>
    );
}

type TableExpansionRowProps = ComponentProps<'tr'> & {
    /** How many columns the table has; the expansion spans them all. */
    colSpan: number;
    /**
     * Edge to edge with no inset box, for an expansion that draws its own bands, as the orders
     * register's does (screens/orders.html). Without it the content sits in the recessed box.
     */
    flush?: boolean;
};

/**
 * The row under an expanded row (the design's .md-exp): one cell across every column, on the
 * recessed surface, holding whatever the row opens into. Below 768 the stacked layout gives a
 * spanning cell the whole card width on its own.
 */
function TableExpansionRow({
    colSpan,
    flush,
    className,
    children,
    ref,
    ...props
}: TableExpansionRowProps) {
    return (
        <tr
            data-slot="table-expansion-row"
            data-flush={flush || undefined}
            className={cn('border-border border-t', className)}
            ref={ref}
            {...props}
        >
            {flush ? (
                <td colSpan={colSpan} className="bg-muted p-0">
                    {children}
                </td>
            ) : (
                <td colSpan={colSpan} className="px-3 py-3 md:pl-[52px]">
                    <div className="bg-muted border-border rounded-md border px-3 py-1">
                        {children}
                    </div>
                </td>
            )}
        </tr>
    );
}

/** Two lines in one cell: primary over secondary, 3px apart. */
function TableCellStack({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="table-cell-stack"
            className={cn(
                '[&>:nth-child(2)]:text-muted-foreground flex min-w-0 flex-col gap-[3px]',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}

/** A number: right-aligned, tabular digits. */
function TableNum({ className, ref, ...props }: ComponentProps<'td'>) {
    return <TableCell className={cn('text-right tabular-nums', className)} ref={ref} {...props} />;
}

/**
 * The row under a register: the count on the left in the design's italic serif, the pager on
 * the right, 44px minimum, 12 above and below as the lookbook register draws it (2758:348). On a phone the two take a line each, centred, and the card's own
 * padding does the insetting.
 */
function TableFooterBar({ className, ref, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="table-footer-bar"
            className={cn(
                'flex min-h-11 items-center justify-between gap-4 px-(--layout-card-padding) py-3',
                '[&>:first-child]:text-display-italic [&>:first-child]:text-muted-foreground',
                'max-md:flex-col max-md:items-center max-md:gap-2 max-md:px-0 max-md:py-3',
                className,
            )}
            ref={ref}
            {...props}
        />
    );
}
function TableCaption({ className, ref, ...props }: ComponentProps<'caption'>) {
    return (
        <caption
            data-slot="table-caption"
            className={cn('text-muted-foreground mt-4 text-sm', className)}
            ref={ref}
            {...props}
        />
    );
}

export {
    Table,
    BleedTables,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableSortHead,
    TableRow,
    TableCell,
    TableCellStack,
    TableExpansionRow,
    TableNum,
    TableFooterBar,
    TableCaption,
};
